import { Link } from "react-router-dom";
import PageShell from "@/components/PageShell";
import PageMeta from "@/components/PageMeta";
import { posts } from "@/blog/posts";
import { Calendar, Clock, ArrowRight } from "@phosphor-icons/react";

export default function Blog() {
  // newest first
  const ordered = [...posts].sort((a, b) => b.date.localeCompare(a.date));
  const [featured, ...rest] = ordered;

  return (
    <PageShell
      eyebrow="Blog"
      title="Writings on file sharing, security, and the modern web."
      subtitle="Practical guides, opinions, and the occasional rant. Updated regularly."
    >
      <PageMeta
        title="Blog · File sharing, security, and workflow guides"
        description="20 long-form articles on file encryption, GDPR, comparing WeTransfer/Dropbox/Drive, freelancer workflows, and more. Practical guides updated regularly."
        keywords="file sharing blog, encryption guide, GDPR file transfer, WeTransfer alternative, secure file sharing"
      />
      {/* Featured */}
      <Link
        to={`/blog/${featured.slug}`}
        data-testid="featured-post"
        className="block brut-card overflow-hidden brut-hover mb-10"
      >
        <div className="grid md:grid-cols-[1.2fr_1fr]">
          <div className="aspect-video md:aspect-auto overflow-hidden border-r border-black/10">
            <img
              src={featured.cover}
              alt=""
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div className="p-6 md:p-8 flex flex-col justify-center">
            <div className="small-caps mb-3 flex items-center gap-3">
              <span className="text-[#FF3366]">Featured</span>
              <span className="flex items-center gap-1"><Calendar size={12}/> {fmtDate(featured.date)}</span>
              <span className="flex items-center gap-1"><Clock size={12}/> {featured.readTime}</span>
            </div>
            <h2 className="text-2xl md:text-3xl tracking-tight mb-3">{featured.title}</h2>
            <p className="text-black/65 leading-relaxed mb-4">{featured.excerpt}</p>
            <span className="inline-flex items-center gap-2 text-sm uppercase tracking-widest text-black">
              Read <ArrowRight size={14} weight="bold"/>
            </span>
          </div>
        </div>
      </Link>

      {/* Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {rest.map((p) => (
          <Link
            key={p.slug}
            to={`/blog/${p.slug}`}
            data-testid={`post-${p.slug}`}
            className="brut-card overflow-hidden brut-hover flex flex-col"
          >
            <div className="aspect-[16/10] overflow-hidden border-b border-black/10">
              <img src={p.cover} alt="" className="w-full h-full object-cover" loading="lazy" />
            </div>
            <div className="p-5 flex flex-col flex-1">
              <div className="small-caps mb-2 flex items-center gap-3">
                <span className="flex items-center gap-1"><Calendar size={11}/> {fmtDate(p.date)}</span>
                <span className="flex items-center gap-1"><Clock size={11}/> {p.readTime}</span>
              </div>
              <h3 className="text-lg font-bold leading-snug tracking-tight mb-2">{p.title}</h3>
              <p className="text-sm text-black/65 leading-relaxed line-clamp-3">{p.excerpt}</p>
              <div className="mt-4 flex flex-wrap gap-1">
                {p.tags.map((t) => (
                  <span key={t} className="text-[10px] uppercase tracking-widest border border-black/15 px-2 py-1 font-mono">{t}</span>
                ))}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </PageShell>
  );
}

function fmtDate(d) {
  return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}
